import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { getPages, parseReport } from '../api';
import ChartCard from '../components/ChartCard';
import ChartTooltip from '../components/ChartTooltip';

const COLORS = ['#38bdf8','#818cf8','#34d399','#f472b6','#fb923c','#fbbf24','#a78bfa','#f87171','#22d3ee','#4ade80'];
function fmtDur(s) { const m=Math.floor(s/60); return `${m}m ${Math.round(s%60)}s`; }

export default function Behavior({ propertyId, startDate, endDate }) {
  const [pages,   setPages]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState('');
  const [sortBy,  setSortBy]  = useState('screenPageViews');
  const [sortDir, setSortDir] = useState('desc');
  const [minViews, setMinViews] = useState(0);

  const load = useCallback(async () => {
    if (!propertyId) return;
    setLoading(true);
    try {
      const res  = await getPages(propertyId, startDate, endDate);
      const rows = parseReport(res.data)
        .sort((a,b) => b.screenPageViews - a.screenPageViews)
        .slice(0,50)
        .map(r => ({ ...r, bounceRate: parseFloat((r.bounceRate*100).toFixed(1)) }));
      setPages(rows);
    } catch(e) { console.error(e); }
    setLoading(false);
  }, [propertyId, startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    let rows = pages.filter(r =>
      (!search || r.pagePath?.toLowerCase().includes(search.toLowerCase()) || r.pageTitle?.toLowerCase().includes(search.toLowerCase()))
      && r.screenPageViews >= minViews
    );
    return [...rows].sort((a,b) => {
      const av=a[sortBy]||0, bv=b[sortBy]||0;
      return sortDir==='desc'?bv-av:av-bv;
    });
  }, [pages, search, sortBy, sortDir, minViews]);

  const toggleSort = col => {
    if(sortBy===col) setSortDir(d=>d==='desc'?'asc':'desc');
    else { setSortBy(col); setSortDir('desc'); }
  };
  const si = col => sortBy===col?(sortDir==='desc'?' ▼':' ▲'):' ↕';

  if(loading) return <Loader />;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:18 }}>

      {/* ── Filter bar ─────────────────────── */}
      <div style={styles.filterBar}>
        <div style={styles.searchWrap}>
          <span style={{ fontSize:13, color:'var(--text3)' }}>🔍</span>
          <input style={styles.searchInput} placeholder="Search page path or title…"
            value={search} onChange={e=>setSearch(e.target.value)} />
          {search && <button style={styles.clearBtn} onClick={()=>setSearch('')}>✕</button>}
        </div>
        <div style={styles.filterGroup}>
          <span style={styles.filterLabel}>Min Views</span>
          <select style={styles.filterSelect} value={minViews} onChange={e=>setMinViews(+e.target.value)}>
            {[0,10,50,100,500,1000].map(v=><option key={v} value={v}>{v===0?'All':'>'+v}</option>)}
          </select>
        </div>
        <span style={styles.resultCount}>{filtered.length} pages</span>
      </div>

      {/* ── Charts row ─────────────────────── */}
      <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr', gap:16 }}>
        <ChartCard title="Top Pages by Views" subtitle="Most visited pages">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={filtered.slice(0,10)} layout="vertical" margin={{top:0,right:20,left:120,bottom:0}}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false}/>
              <XAxis type="number" tick={{fill:'var(--text3)',fontSize:10}}/>
              <YAxis type="category" dataKey="pagePath"
                tickFormatter={v=>v?.length>18?v.slice(0,18)+'…':v}
                tick={{fill:'var(--text2)',fontSize:10}} width={115}/>
              <Tooltip content={<ChartTooltip/>}/>
              <Bar dataKey="screenPageViews" name="Views" radius={[0,4,4,0]}>
                {filtered.slice(0,10).map((_,i)=><Cell key={i} fill={COLORS[i%COLORS.length]}/>)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Bounce Rate" subtitle="Lower is better">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={filtered.slice(0,8)} margin={{top:5,right:10,left:0,bottom:40}}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)"/>
              <XAxis dataKey="pagePath" tickFormatter={v=>v?.slice(0,10)} tick={{fill:'var(--text3)',fontSize:9,angle:-30,textAnchor:'end'}}/>
              <YAxis tick={{fill:'var(--text3)',fontSize:10}} unit="%"/>
              <Tooltip content={<ChartTooltip/>}/>
              <Bar dataKey="bounceRate" name="Bounce %" radius={[4,4,0,0]}>
                {filtered.slice(0,8).map((r,i)=><Cell key={i} fill={r.bounceRate<40?'#34d399':r.bounceRate<70?'#fbbf24':'#f87171'}/>)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Active Users" subtitle="Per page">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={filtered.slice(0,8)} margin={{top:5,right:10,left:0,bottom:40}}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)"/>
              <XAxis dataKey="pagePath" tickFormatter={v=>v?.slice(0,10)} tick={{fill:'var(--text3)',fontSize:9,angle:-30,textAnchor:'end'}}/>
              <YAxis tick={{fill:'var(--text3)',fontSize:10}}/>
              <Tooltip content={<ChartTooltip/>}/>
              <Bar dataKey="activeUsers" name="Active Users" fill="#818cf8" radius={[4,4,0,0]}/>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* ── Table ──────────────────────────── */}
      <div style={styles.tableWrap}>
        <div style={styles.thead}>
          {[
            {key:'#',label:'#',sort:false},{key:'pagePath',label:'Page Path',sort:true},
            {key:'pageTitle',label:'Title',sort:false},{key:'screenPageViews',label:'Views',sort:true},
            {key:'activeUsers',label:'Users',sort:true},{key:'averageSessionDuration',label:'Avg Time',sort:true},
            {key:'bounceRate',label:'Bounce %',sort:true},
          ].map(h=>(
            <span key={h.key} style={{cursor:h.sort?'pointer':'default'}} onClick={()=>h.sort&&toggleSort(h.key)}>
              {h.label}{h.sort?si(h.key):''}
            </span>
          ))}
        </div>
        {filtered.length===0
          ? <div style={{padding:30,textAlign:'center',color:'var(--text3)',fontSize:13}}>No pages match your filter</div>
          : filtered.map((r,i)=>(
            <div key={i} style={{...styles.trow, background:i%2===0?'transparent':'rgba(99,179,237,0.02)'}}>
              <span style={{color:'var(--text3)',fontSize:11}}>{i+1}</span>
              <span style={{color:'var(--accent)',fontFamily:'Space Mono,monospace',fontSize:11}}
                title={r.pagePath}>{r.pagePath?.length>28?r.pagePath.slice(0,28)+'…':r.pagePath}</span>
              <span style={{color:'var(--text2)',fontSize:11}}>{r.pageTitle?.slice(0,20)||'—'}</span>
              <span style={{fontWeight:600,fontFamily:'Space Mono,monospace'}}>{r.screenPageViews?.toLocaleString()}</span>
              <span style={{fontFamily:'Space Mono,monospace'}}>{r.activeUsers?.toLocaleString()}</span>
              <span style={{fontFamily:'Space Mono,monospace'}}>{fmtDur(r.averageSessionDuration||0)}</span>
              <span style={{color:r.bounceRate<40?'var(--success)':r.bounceRate<70?'var(--warning)':'var(--danger)',fontWeight:600}}>
                {r.bounceRate}%
              </span>
            </div>
          ))
        }
      </div>
    </div>
  );
}

function Loader(){return<div style={{display:'flex',alignItems:'center',justifyContent:'center',height:300,flexDirection:'column',gap:12}}><div style={{fontSize:36,color:'var(--accent)',animation:'spin 1s linear infinite'}}>◈</div><div style={{color:'var(--text3)',fontSize:13}}>Loading pages…</div></div>;}

const styles={
  filterBar:{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:12,padding:'12px 16px',display:'flex',alignItems:'center',gap:14,flexWrap:'wrap'},
  searchWrap:{display:'flex',alignItems:'center',gap:8,background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:9,padding:'8px 12px',flex:1,minWidth:220},
  searchInput:{flex:1,background:'none',border:'none',outline:'none',color:'var(--text)',fontSize:13,fontFamily:'Sora,sans-serif'},
  clearBtn:{background:'none',border:'none',cursor:'pointer',color:'var(--text3)',fontSize:12,padding:0},
  filterGroup:{display:'flex',alignItems:'center',gap:8},
  filterLabel:{fontSize:11,color:'var(--text3)',fontWeight:600,whiteSpace:'nowrap'},
  filterSelect:{background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:8,color:'var(--text)',padding:'7px 10px',fontSize:12,outline:'none'},
  resultCount:{fontSize:12,color:'var(--text3)',marginLeft:'auto'},
  tableWrap:{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:14,overflow:'hidden'},
  thead:{display:'grid',gridTemplateColumns:'36px 2fr 1.5fr 1fr 1fr 1fr 1fr',padding:'10px 16px',fontSize:10,color:'var(--text3)',fontWeight:700,letterSpacing:'0.08em',textTransform:'uppercase',background:'var(--surface2)',borderBottom:'1px solid var(--border)',gap:8},
  trow:{display:'grid',gridTemplateColumns:'36px 2fr 1.5fr 1fr 1fr 1fr 1fr',padding:'10px 16px',fontSize:12,color:'var(--text2)',borderBottom:'1px solid var(--border2)',gap:8,alignItems:'center'},
};
